/**
 * OptionProfilePrimitive — a right-docked "option profile" that shares the price
 * Y-axis (item 3). For one expiry tier it draws, per strike, horizontal bars
 * anchored to the RIGHT edge of the plot area at y = priceToCoordinate(strike), so
 * the option structure lines up with price. One metric at a time to stay uncluttered:
 *   • OI    — CE (resistance) bar above the row, PE (support) bar below, length ∝ OI
 *   • ΔOI   — CE/PE change bars coloured by build (green) / unwind (red)
 *   • GEX   — one bar per strike, green (+γ) / red (−γ), length ∝ |GEX|
 * Plus right-edge ticks for Max Pain (◆) and the GEX flip (▸). Bars are normalised
 * to the largest VISIBLE value, faded by distance from spot, and labelled only on
 * the few biggest — all reachable, never cluttered.
 */
import type {
  ISeriesPrimitive, IPrimitivePaneView, IPrimitivePaneRenderer,
  SeriesAttachedParameter, Time, IChartApi, ISeriesApi, SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export type ProfileMode = 'OI' | 'OIChg' | 'GEX' | 'Combined';
export interface OptionProfilePoint { strike: number; ceOi: number; peOi: number; ceOiChg: number; peOiChg: number; gex: number; }
/** Futures OI-by-price footprint bucket: net ΔOI at a price level. */
export interface FuturesProfileBucket { price: number; net: number; }
export interface OptionProfileData {
  mode: ProfileMode; widthPx: number; points: OptionProfilePoint[];
  spot: number; maxPain: number | null; gexFlip: number | null;
  /** Optional futures OI-by-price footprint → leftmost lane in Combined mode. */
  futures?: FuturesProfileBucket[];
  /** ΔOI reference window (ISO) — shown under the ΔOI lane header. */
  deltaFrom?: string | null; deltaTo?: string | null;
}
export interface OptionProfileColors {
  ce: string; pe: string; gexPos: string; gexNeg: string; build: string; unwind: string;
  text: string; railBg: string; maxPain: string; flip: string;
}

const withAlpha = (rgbCss: string, a: number) => {
  // Accepts #rrggbb or rgb()/rgba(); returns rgba with alpha a.
  if (rgbCss.startsWith('#')) {
    const n = parseInt(rgbCss.slice(1), 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
  }
  const m = rgbCss.match(/(\d+)\D+(\d+)\D+(\d+)/);
  return m ? `rgba(${m[1]},${m[2]},${m[3]},${a})` : rgbCss;
};

class ProfileRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly data: OptionProfileData,
    private readonly colors: OptionProfileColors,
    private readonly chart: IChartApi,
    private readonly series: ISeriesApi<SeriesType>,
  ) {}
  draw(target: CanvasRenderingTarget2D): void {
    const d = this.data;
    if (!d || !d.points.length) return;
    target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const W = bitmapSize.width, H = bitmapSize.height;
      const rail = Math.min(d.widthPx, 220) * hpr;      // bitmap px
      const rightX = W;                                  // plot-area right edge (axis is outside)
      const spot = d.spot || 0;

      // y for each strike (skip off-pane), and adaptive row height from strike spacing.
      const rows = d.points
        .map(p => ({ p, y: this.series.priceToCoordinate(p.strike) }))
        .filter(r => r.y != null && Number(r.y) * vpr >= -4 && Number(r.y) * vpr <= H + 4)
        .map(r => ({ p: r.p, y: Number(r.y) * vpr }));
      if (rows.length < 1) return;
      let minGap = Infinity;
      for (let i = 1; i < rows.length; i++) minGap = Math.min(minGap, Math.abs(rows[i].y - rows[i - 1].y));
      const rowH = Math.max(2 * vpr, Math.min((Number.isFinite(minGap) ? minGap : 12 * vpr) * 0.8, 16 * vpr));

      // Near-opaque rail panel so the profiles stand out from the candles behind, with a
      // subtle left divider framing it (theme-aware via the text colour).
      ctx.fillStyle = this.colors.railBg;
      ctx.fillRect(rightX - rail, 0, rail, H);
      ctx.fillStyle = withAlpha(this.colors.text, 0.28);
      ctx.fillRect(rightX - rail, 0, Math.max(1, hpr), H);
      const fade = (strike: number) => {
        if (spot <= 0) return 1;
        const dist = Math.abs(strike - spot) / spot;
        return Math.max(0.3, Math.min(1, 1 - dist / 0.18));
      };
      const half = rowH / 2;
      // Gradient bar — solid at the lane anchor, fading to transparent at the tip;
      // alpha is also value-weighted (bigger = more solid) for a sophisticated look.
      const minLen = 2 * hpr; // every non-zero value shows at least a sliver (never invisible)
      const gbar = (anchor: number, len: number, color: string, yTop: number, h: number, alpha: number) => {
        if (len < 0.5) return;
        len = Math.max(len, minLen);
        const grad = ctx.createLinearGradient(anchor, 0, anchor - len, 0);
        grad.addColorStop(0, withAlpha(color, alpha));
        grad.addColorStop(1, withAlpha(color, alpha * 0.12));
        ctx.fillStyle = grad;
        ctx.fillRect(anchor - len, yTop, len, Math.max(1, h - 1));
      };

      if (d.mode === 'Combined') {
        // THREE separate side-by-side profiles sharing the strike axis, right→left:
        //   OI  (CE wall top / PE wall bottom)      — anchored at the price axis
        //   ΔOI (NET support vs resistance shift) — green = support added, red = resistance added
        //   GEX (γ walls: +γ green / −γ red)         — with the GEX-flip line drawn across it
        // Each lane is normalised independently (√ scaling lifts the many small bars).
        // ΔOI net per strike = ΔPE (support change) − ΔCE (resistance change): positive ⇒
        // more support was added than resistance (bullish, green); negative ⇒ the reverse.
        const netChg = (p: OptionProfilePoint) => (p.peOiChg || 0) - (p.ceOiChg || 0);
        let maxOI = 0, maxNet = 0, maxGex = 0;
        for (const r of rows) {
          maxOI = Math.max(maxOI, r.p.ceOi, r.p.peOi);
          maxNet = Math.max(maxNet, Math.abs(netChg(r.p)));
          maxGex = Math.max(maxGex, Math.abs(r.p.gex));
        }
        const hasFut = !!(d.futures && d.futures.length);
        if (maxOI <= 0 && maxGex <= 0 && maxNet <= 0 && !hasFut) return;
        const laneCount = hasFut ? 4 : 3;
        const gap = Math.max(2 * hpr, rail * 0.025);
        const laneW = (rail - (laneCount - 1) * gap) / laneCount;
        const anchorOf = (i: number) => rightX - i * (laneW + gap);
        const oiAnchor = anchorOf(0), chgAnchor = anchorOf(1), gexAnchor = anchorOf(2), futAnchor = anchorOf(3);
        // Faint lane dividers.
        ctx.fillStyle = withAlpha(this.colors.text, 0.1);
        ctx.fillRect(chgAnchor, 0, Math.max(1, hpr), H);
        ctx.fillRect(gexAnchor, 0, Math.max(1, hpr), H);
        if (hasFut) ctx.fillRect(futAnchor, 0, Math.max(1, hpr), H);
        const lane = (anchor: number, value: number, maxV: number, color: string, yTop: number, h: number, baseAlpha: number): void => {
          if (value <= 0 || maxV <= 0) return;
          const frac = value / maxV;
          const len = Math.sqrt(frac) * laneW;
          const alpha = Math.min(1, baseAlpha * (0.55 + 0.45 * Math.sqrt(frac)));
          gbar(anchor, len, color, yTop, h, alpha);
        };
        for (const r of rows) {
          const p = r.p, a = fade(p.strike);
          // OI lane (CE top / PE bottom).
          lane(oiAnchor, p.ceOi, maxOI, this.colors.ce, r.y - half, half, a * 0.9);
          lane(oiAnchor, p.peOi, maxOI, this.colors.pe, r.y, half, a * 0.9);
          // ΔOI lane — ONE bar = NET support/resistance shift (ΔPE − ΔCE).
          // green = net support added (bullish), red = net resistance added (bearish).
          const net = netChg(p);
          lane(chgAnchor, Math.abs(net), maxNet, net >= 0 ? this.colors.build : this.colors.unwind, r.y - half, rowH, Math.min(1, a + 0.1));
          // GEX lane (full-row bar, +γ green / −γ red).
          lane(gexAnchor, Math.abs(p.gex), maxGex, p.gex >= 0 ? this.colors.gexPos : this.colors.gexNeg, r.y - half, rowH, a * 0.95);
        }
        // Futures OI-by-price lane (leftmost) — green = net build, red = net unwind.
        if (hasFut) {
          const futRows = d.futures!
            .map(b => ({ b, y: this.series.priceToCoordinate(b.price) }))
            .filter(r => r.y != null && Number(r.y) * vpr >= -4 && Number(r.y) * vpr <= H + 4)
            .map(r => ({ b: r.b, y: Number(r.y) * vpr }));
          let maxFut = 0; for (const r of futRows) maxFut = Math.max(maxFut, Math.abs(r.b.net));
          let fgap = Infinity; for (let i = 1; i < futRows.length; i++) fgap = Math.min(fgap, Math.abs(futRows[i].y - futRows[i - 1].y));
          const fh = Math.max(2 * vpr, Math.min((Number.isFinite(fgap) ? fgap : 14 * vpr) * 0.85, 20 * vpr));
          for (const r of futRows) {
            const a = fade(r.b.price);
            lane(futAnchor, Math.abs(r.b.net), maxFut, r.b.net >= 0 ? this.colors.gexPos : this.colors.gexNeg, r.y - fh / 2, fh, a * 0.9);
          }
        }
        // GEX-flip line across the GEX lane (the regime boundary on the γ profile).
        if (d.gexFlip != null) {
          const yc = this.series.priceToCoordinate(d.gexFlip);
          if (yc != null) {
            const y = Number(yc) * vpr;
            ctx.strokeStyle = this.colors.flip; ctx.lineWidth = Math.max(1, 1.4 * vpr);
            ctx.setLineDash([4 * hpr, 3 * hpr]);
            ctx.beginPath(); ctx.moveTo(gexAnchor - laneW, y); ctx.lineTo(gexAnchor, y); ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = this.colors.flip;
            ctx.font = `700 ${Math.round(8 * Math.min(hpr, vpr))}px ui-sans-serif, system-ui, sans-serif`;
            ctx.textAlign = 'left'; ctx.textBaseline = 'bottom';
            ctx.fillText('flip', gexAnchor - laneW + 1, y - 1);
          }
        }
        // Per-lane headers.
        ctx.font = `800 ${Math.round(8 * Math.min(hpr, vpr))}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textBaseline = 'top'; ctx.textAlign = 'right';
        ctx.fillStyle = withAlpha(this.colors.text, 0.85);
        ctx.fillText('OI', oiAnchor - 2, 3 * vpr);
        ctx.fillText('ΔOI', chgAnchor - 2, 3 * vpr);
        ctx.fillText('γ', gexAnchor - 2, 3 * vpr);
        if (hasFut) ctx.fillText('Fut', futAnchor - 2, 3 * vpr);
        // ΔOI reference window (IST HH:MM–HH:MM) under the ΔOI header so it's clear
        // the build/unwind is measured since the session's first snapshot.
        const istHM = (iso?: string | null) => {
          if (!iso) return null;
          const t = Date.parse(iso); if (!Number.isFinite(t)) return null;
          const d = new Date(t + 5.5 * 3600 * 1000);
          return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
        };
        const wFrom = istHM(d.deltaFrom), wTo = istHM(d.deltaTo);
        if (wFrom && wTo) {
          ctx.font = `600 ${Math.round(6.5 * Math.min(hpr, vpr))}px ui-sans-serif, system-ui, sans-serif`;
          ctx.fillStyle = withAlpha(this.colors.text, 0.6);
          ctx.fillText(`${wFrom}–${wTo}`, chgAnchor - 2, 12 * vpr);
        }
      } else {
        // Single-metric: GEX = one bar; OI/ΔOI = CE (top) + PE (bottom). Gradient + value opacity.
        const val = (p: OptionProfilePoint): { a: number; b: number; ca: string; cb: string } => {
          if (d.mode === 'GEX') { const g = p.gex; return { a: Math.abs(g), b: 0, ca: g >= 0 ? this.colors.gexPos : this.colors.gexNeg, cb: 'transparent' }; }
          if (d.mode === 'OIChg') { const net = (p.peOiChg || 0) - (p.ceOiChg || 0); return { a: Math.abs(net), b: 0, ca: net >= 0 ? this.colors.build : this.colors.unwind, cb: 'transparent' }; }
          return { a: p.ceOi, b: p.peOi, ca: this.colors.ce, cb: this.colors.pe };
        };
        let maxV = 0;
        for (const r of rows) { const v = val(r.p); maxV = Math.max(maxV, v.a, v.b); }
        if (maxV <= 0) return;
        const drawBar = (value: number, color: string, yTop: number, h: number, baseAlpha: number) => {
          if (value <= 0 || color === 'transparent') return;
          const frac = value / maxV;
          const len = Math.sqrt(frac) * rail;   // perceptual scaling (skewed OI)
          const alpha = Math.min(1, baseAlpha * (0.55 + 0.45 * Math.sqrt(frac)));
          gbar(rightX, len, color, yTop, h, alpha);
        };
        for (const r of rows) {
          const v = val(r.p); const a = fade(r.p.strike);
          if (d.mode === 'GEX') drawBar(v.a, v.ca, r.y - half, rowH, a);
          else { drawBar(v.a, v.ca, r.y - half, half, a); drawBar(v.b, v.cb, r.y, half, a); }
        }
      }

      // (No value labels on profiles — bar length + colour + opacity carry the read;
      //  the crosshair tooltip shows the exact numbers.)

      // Max-pain ◆ and GEX-flip ▸ right-edge ticks.
      const tick = (price: number | null, color: string, glyph: string) => {
        if (price == null) return;
        const yc = this.series.priceToCoordinate(price);
        if (yc == null) return;
        const y = Number(yc) * vpr;
        ctx.fillStyle = color;
        ctx.font = `700 ${Math.round(10 * Math.min(hpr, vpr))}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
        ctx.fillText(glyph, rightX - 2, y);
      };
      tick(d.maxPain, this.colors.maxPain, '◆');
      if (d.mode !== 'Combined') tick(d.gexFlip, this.colors.flip, '▸'); // Combined draws the flip LINE on the γ lane

      // Header chip (mode) — Combined draws its own per-lane headers (OI/ΔOI/γ).
      if (d.mode !== 'Combined') {
        ctx.fillStyle = this.colors.text;
        ctx.font = `800 ${Math.round(9 * Math.min(hpr, vpr))}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textAlign = 'right'; ctx.textBaseline = 'top';
        ctx.fillText(d.mode === 'GEX' ? 'GEX' : d.mode === 'OIChg' ? 'ΔOI' : 'OI', rightX - 4, 3 * vpr);
      }
    });
  }
  private fmt(v: number): string {
    const a = Math.abs(v);
    if (a >= 1e7) return (v / 1e7).toFixed(1) + 'Cr';
    if (a >= 1e5) return (v / 1e5).toFixed(1) + 'L';
    if (a >= 1e3) return (v / 1e3).toFixed(1) + 'k';
    return String(Math.round(v));
  }
}

class ProfilePaneView implements IPrimitivePaneView {
  constructor(private readonly parent: OptionProfilePrimitive) {}
  zOrder() { return 'top' as const; }
  renderer(): IPrimitivePaneRenderer | null {
    const { chart, series, data, colors } = this.parent;
    return chart && series && data ? new ProfileRenderer(data, colors, chart, series) : null;
  }
}

export class OptionProfilePrimitive implements ISeriesPrimitive<Time> {
  data: OptionProfileData | null = null;
  colors: OptionProfileColors;
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  private readonly _paneViews: IPrimitivePaneView[];

  constructor(colors: OptionProfileColors, data: OptionProfileData | null = null) {
    this.colors = colors; this.data = data;
    this._paneViews = [new ProfilePaneView(this)];
  }
  attached(params: SeriesAttachedParameter<Time>): void { this.chart = params.chart as IChartApi; this.series = params.series as ISeriesApi<SeriesType>; }
  detached(): void { this.chart = null; this.series = null; }
  updateAllViews(): void { /* renderer reads fresh */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._paneViews; }
  setData(data: OptionProfileData | null): void { this.data = data; this.chart?.applyOptions({}); }
  setColors(colors: OptionProfileColors): void { this.colors = colors; this.chart?.applyOptions({}); }
}
